import React, { useState, useEffect } from 'react';
import { auditAPI } from '../services/api';
import { FiList, FiSearch, FiFilter, FiDownload, FiShield, FiAlertTriangle, FiCheckCircle, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', user: '', page: 1 });
  const [total, setTotal] = useState(0);

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        auditAPI.getLogs(filters),
        auditAPI.getStats().catch(() => ({ data: {} }))
      ]);
      setLogs(logsRes.data.logs || logsRes.data || []);
      setTotal(logsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleExport = () => {
    try {
      const csv = [
        ['Timestamp', 'User', 'Action', 'Resource', 'Details', 'IP'].join(','),
        ...logs.map(l => [
          new Date(l.createdAt).toISOString(),
          l.user?.firstName ? `${l.user.firstName} ${l.user.lastName}` : l.userId,
          l.action, l.resource, `"${(l.details || '').replace(/"/g, '""')}"`, l.ipAddress
        ].join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'audit_logs.csv'; a.click();
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  const actionColor = (action) => {
    if (action?.includes('create') || action?.includes('add')) return { bg: '#dcfce7', color: '#166534' };
    if (action?.includes('delete') || action?.includes('remove')) return { bg: '#fee2e2', color: '#991b1b' };
    if (action?.includes('update') || action?.includes('edit')) return { bg: '#dbeafe', color: '#1e40af' };
    if (action?.includes('login') || action?.includes('auth')) return { bg: '#fef3c7', color: '#854d0e' };
    return { bg: '#f1f5f9', color: '#475569' };
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Audit & Compliance</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Activity logs, HIPAA compliance, and data access tracking</p>
        </div>
        <button onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          <FiDownload size={16} /> Export Logs
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { icon: FiList, label: 'Total Events', value: total || stats?.totalLogs || 0, color: '#3b82f6' },
          { icon: FiClock, label: 'Today', value: stats?.today || 0, color: '#10b981' },
          { icon: FiShield, label: 'Auth Events', value: stats?.authEvents || 0, color: '#f59e0b' },
          { icon: FiAlertTriangle, label: 'Critical Actions', value: stats?.critical || 0, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <s.icon size={20} color={s.color} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{s.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* HIPAA Compliance Checklist */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>HIPAA Compliance Checklist</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
          {[
            { item: 'Access Controls & Authentication', status: true },
            { item: 'Audit Trail Logging', status: true },
            { item: 'Data Encryption at Rest', status: true },
            { item: 'Data Encryption in Transit (HTTPS)', status: true },
            { item: 'Role-Based Access Control', status: true },
            { item: 'Password Policy Enforcement', status: true },
            { item: 'Session Management & Timeout', status: true },
            { item: 'Data Backup & Recovery Plan', status: false },
            { item: 'Business Associate Agreements', status: false },
            { item: 'Employee Training Records', status: false },
            { item: 'Incident Response Plan', status: false },
            { item: 'Data Retention Policy', status: true },
          ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#f8fafc' }}>
              {c.status ? <FiCheckCircle size={16} color="#10b981" /> : <FiAlertTriangle size={16} color="#f59e0b" />}
              <span style={{ fontSize: 13, fontWeight: 500, color: c.status ? '#166534' : '#92400e' }}>{c.item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar" style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <select value={filters.action} onChange={e => setFilters({ ...filters, action: e.target.value, page: 1 })}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}>
          <option value="">All Actions</option>
          {['login', 'create', 'update', 'delete', 'view', 'approve', 'reject', 'export'].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* Logs Table */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Timestamp', 'User', 'Action', 'Resource', 'Details', 'IP Address'].map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => {
                const ac = actionColor(log.action);
                return (
                  <tr key={log._id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                      {log.user?.firstName ? `${log.user.firstName} ${log.user.lastName}` : 'System'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: ac.bg, color: ac.color }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{log.resource}</td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.details}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>
                      {log.ipAddress || '—'}
                    </td>
                  </tr>
                );
              })}
              {logs.length === 0 && <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No audit logs found</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 50 && (
          <div className="pagination-bar" style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Showing {logs.length} of {total}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })} disabled={filters.page === 1}
                style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Prev</button>
              <button onClick={() => setFilters({ ...filters, page: filters.page + 1 })} disabled={logs.length < 50}
                style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
